import { DocumentData, QuerySnapshot } from '@google-cloud/firestore';
import { flow, Instance, SnapshotOut, types } from 'mobx-state-tree';

import { MusicProviders } from '../types/shared';
import firebaseService from './../services/firebaseService';

export interface ShareLinkParams {
    userId: string;
    playlistId: string;
}

const ConfigStore = types
    .model('ConfigStore', {
        uid: types.optional(types.string, ''),
        guestsLimit: types.optional(types.number, 5),
        shareLink: types.optional(types.string, ''),
        tracksLimit: types.optional(types.number, 50),
        playlistName: types.optional(types.string, ''),
    })
    .volatile(() => ({
        isLinkValid: false,
        currentTrackColors: [''],
        sidebarWidth: 500,
        footerHeight: 75,
        headerHeight: 80,
        isBrowseMusicPanelOpen: false,
        isSharePanelOpen: false,
        isConfigPanelOpen: false,
        isQueuePanelOpenOnMobile: false,
    }))
    .views(self => ({
        get isTopPanelOpen() {
            return self.isBrowseMusicPanelOpen || self.isConfigPanelOpen;
        },
        get isBottomPanelOpen() {
            return self.isQueuePanelOpenOnMobile || self.isSharePanelOpen;
        },
    }))
    .actions(self => {
        const setConfig = (config: IConfigStoreSnapshotOut) => {
            self.uid = config.uid;
            self.guestsLimit = config.guestsLimit;
            self.shareLink = config.shareLink;
            self.tracksLimit = config.tracksLimit;
            self.playlistName = config.playlistName;
        };

        const setPartyUID = (uid: string) => {
            self.uid = uid;
        };

        const validatePartyLink = flow(function*(routeParams: ShareLinkParams) {
            // TODO: this could be refactored to hit the DB only if the link is not validated yet
            // Probably move call this from AuthStore, after guest signin
            const partyEntity: QuerySnapshot<DocumentData> = yield firebaseService.validatePartyLink(
                `${MusicProviders.SPOTIFY}:${routeParams.userId}:${routeParams.playlistId}`
            );
            if (partyEntity.empty) {
                // Party does not exists
                self.isLinkValid = false;
                return false;
            }
            self.isLinkValid = true;
            return true;
        });

        const setCurrentTrackColors = (colors: string[] | number[][]) => {
            if (colors.length > 1) {
                const primaryColor = colors[0];
                const secondaryColor = colors[1];
                const thirdColor = colors[2];
                if (typeof primaryColor === 'string' && typeof secondaryColor === 'string' && typeof thirdColor === 'string') {
                    self.currentTrackColors = [primaryColor, secondaryColor, thirdColor];
                }
            }
        };

        const toggleBrowseMusicPanel = () => {
            self.isSharePanelOpen = false;
            self.isConfigPanelOpen = false;
            self.isQueuePanelOpenOnMobile = false;
            self.isBrowseMusicPanelOpen = !self.isBrowseMusicPanelOpen;
        };

        const toggleSharePanel = () => {
            self.isBrowseMusicPanelOpen = false;
            self.isConfigPanelOpen = false;
            self.isQueuePanelOpenOnMobile = false;
            self.isSharePanelOpen = !self.isSharePanelOpen;
        };

        const toggleConfigPanel = () => {
            self.isBrowseMusicPanelOpen = false;
            self.isSharePanelOpen = false;
            self.isQueuePanelOpenOnMobile = false;
            self.isConfigPanelOpen = !self.isConfigPanelOpen;
        };

        const closeAllPanels = () => {
            self.isBrowseMusicPanelOpen = false;
            self.isSharePanelOpen = false;
            self.isConfigPanelOpen = false;
            self.isQueuePanelOpenOnMobile = false;
        };

        const toggleQueuePanelOnMobile = () => {
            self.isBrowseMusicPanelOpen = false;
            self.isSharePanelOpen = false;
            self.isConfigPanelOpen = false;
            self.isQueuePanelOpenOnMobile = !self.isQueuePanelOpenOnMobile;
        };

        return {
            setConfig,
            validatePartyLink,
            setPartyUID,
            setCurrentTrackColors,
            toggleBrowseMusicPanel,
            toggleSharePanel,
            toggleConfigPanel,
            closeAllPanels,
            toggleQueuePanelOnMobile,
        };
    });

export interface IConfigStore extends Instance<typeof ConfigStore> {}
export interface IConfigStoreSnapshotOut extends SnapshotOut<typeof ConfigStore> {}

export default ConfigStore;
